/**
 * TemplateSelection Component
 *
 * Shows core WordPress theme templates (always available) plus industry-specific
 * page templates fetched from the API when an industry is selected.
 *
 * Core templates feed into the TemplateCustomizer step.
 * Industry pages use pre-built templates directly (no per-page customization).
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Alert,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Button,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import LockIcon from '@mui/icons-material/Lock';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const CORE_TEMPLATE_IDS = new Set(['home', 'blog', 'single', 'about', 'services', 'contact', 'gallery', '404', 'search']);

/**
 * Core WordPress theme templates available for inclusion.
 * Required templates are always enabled and cannot be deselected.
 * Optional templates can be toggled on/off by the user.
 */
const CORE_TEMPLATES = [
  {
    id: 'home',
    name: 'Homepage',
    wpFile: 'front-page.php',
    description: 'The main landing/front page of your site.',
    required: true,
    icon: '🏠'
  },
  {
    id: 'blog',
    name: 'Blog / Archive',
    wpFile: 'archive.php',
    description: 'Blog post listing and archive pages.',
    required: false,
    icon: '📝'
  },
  {
    id: 'single',
    name: 'Single Post',
    wpFile: 'single.php',
    description: 'Individual blog post or article layout.',
    required: false,
    icon: '📄'
  },
  {
    id: 'about',
    name: 'About',
    wpFile: 'page-templates/about.php',
    description: 'About your business, team, or story.',
    required: false,
    icon: '👥'
  },
  {
    id: 'services',
    name: 'Services',
    wpFile: 'page-templates/services.php',
    description: 'Showcase your products or services.',
    required: false,
    icon: '🛠️'
  },
  {
    id: 'contact',
    name: 'Contact',
    wpFile: 'page-templates/contact.php',
    description: 'Contact form and business location/details.',
    required: false,
    icon: '📬'
  },
  {
    id: 'gallery',
    name: 'Gallery / Portfolio',
    wpFile: 'page-templates/gallery.php',
    description: 'Visual showcase of work or photos.',
    required: false,
    icon: '🖼️'
  },
  {
    id: '404',
    name: '404 Error Page',
    wpFile: '404.php',
    description: 'Custom "page not found" layout for broken links.',
    required: false,
    icon: '🚧'
  },
  {
    id: 'search',
    name: 'Search Results',
    wpFile: 'search.php',
    description: 'Template shown when visitors search your site.',
    required: false,
    icon: '🔍'
  }
];

const REQUIRED_TEMPLATE_IDS = CORE_TEMPLATES.filter(t => t.required).map(t => t.id);

/**
 * Supplemental templates — included automatically with every generated theme.
 * These are styled by the theme's global design system and are NOT individually
 * configurable in the wizard.
 *
 * These templates are intentionally NOT rendered in the selection UI to avoid
 * showing a long list of non-interactive items. They are kept here so downstream
 * code (e.g. tests, future tooling) can reference the canonical list.
 */
const SUPPLEMENTAL_TEMPLATES = [
  {
    id: 'faq',
    name: 'FAQ',
    wpFile: 'page-templates/faq.php',
    description: 'Frequently asked questions page.',
    icon: '❓'
  },
  {
    id: 'pricing',
    name: 'Pricing / Rates',
    wpFile: 'page-templates/pricing.php',
    description: 'Pricing table or rate overview.',
    icon: '💲'
  },
  {
    id: 'team',
    name: 'Team Directory',
    wpFile: 'page-templates/team.php',
    description: 'Meet-the-team or staff directory.',
    icon: '👤'
  },
  {
    id: 'testimonials-page',
    name: 'Testimonials',
    wpFile: 'page-templates/testimonials.php',
    description: 'Dedicated client reviews page.',
    icon: '⭐'
  },
  {
    id: 'shop',
    name: 'Shop / Products',
    wpFile: 'woocommerce/archive-product.php',
    description: 'WooCommerce shop page (auto-enabled when WooCommerce is active).',
    icon: '🛒'
  },
  {
    id: 'events',
    name: 'Events / Calendar',
    wpFile: 'page-templates/events.php',
    description: 'Events listing or calendar view.',
    icon: '📅'
  },
  {
    id: 'landing',
    name: 'Landing Page',
    wpFile: 'page-templates/landing.php',
    description: 'Minimal full-width template (no header/footer) for campaigns.',
    icon: '🚀'
  },
  {
    id: 'coming-soon',
    name: 'Coming Soon / Maintenance',
    wpFile: 'page-templates/coming-soon.php',
    description: 'Placeholder page shown before your site goes live.',
    icon: '🔒'
  }
];

/**
 * TemplateSelection
 *
 * @param {string[]} value            - Array of selected template IDs (core + industry)
 * @param {function} onChange         - Callback with new array of selected IDs
 * @param {string}   industry         - Industry slug for fetching industry-specific pages
 * @param {string}   specialization   - Specialization slug (optional)
 */
const TemplateSelection = ({ value = [], onChange, industry = null, specialization = null }) => {
  const [industryPages, setIndustryPages] = useState([]);
  const [loadingIndustry, setLoadingIndustry] = useState(false);

  useEffect(() => {
    if (!industry) {
      setIndustryPages([]);
      return;
    }
    setLoadingIndustry(true);
    const industrySlug = typeof industry === 'string' ? industry : industry.slug;
    const specSlug = specialization
      ? (typeof specialization === 'string' ? specialization : specialization.slug)
      : null;
    const url = specSlug
      ? `/api/industries/${industrySlug}/pages?specialization=${specSlug}`
      : `/api/industries/${industrySlug}/pages`;

    axios.get(url)
      .then(res => {
        // Exclude base page IDs already covered by core templates
        const filtered = (res.data.pages || []).filter(p => !CORE_TEMPLATE_IDS.has(p.id));
        setIndustryPages(filtered);
      })
      .catch(() => setIndustryPages([]))
      .finally(() => setLoadingIndustry(false));
  }, [industry, specialization]);

  // Group industry pages by category
  const industryByCategory = industryPages.reduce((acc, page) => {
    const cat = page.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(page);
    return acc;
  }, {});

  const handleToggle = (templateId, required) => {
    if (required) return;
    if (value.includes(templateId)) {
      onChange(value.filter(id => id !== templateId));
    } else {
      onChange([...value, templateId]);
    }
  };

  const handleSelectAllCategory = (categoryPages) => {
    const ids = categoryPages.map(p => p.id);
    const allSelected = ids.every(id => value.includes(id));
    if (allSelected) {
      onChange(value.filter(id => !ids.includes(id)));
    } else {
      onChange([...new Set([...value, ...ids])]);
    }
  };

  const selectedCount = value.length;

  return (
    <Box sx={{ my: 2 }}>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        Select Theme Templates
      </Typography>

      <Alert severity="info" icon={false} sx={{ mb: 2 }}>
        <Typography variant="body2" fontWeight="medium" gutterBottom>
          🎨 You are building a <strong>WordPress theme</strong>, not a complete website.
        </Typography>
        <Typography variant="body2">
          Select which <strong>template files</strong> to include in your theme. Each template
          controls how a type of content looks — your actual pages and posts are created later
          inside <strong>WordPress Admin</strong> after you install the theme.
        </Typography>
      </Alert>

      <Alert severity="warning" icon={false} sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight="medium" gutterBottom>
          ✏️ Core templates (below) are <strong>fully customizable</strong> — choose layouts,
          toggle sections, and fine-tune every detail in the next step.
        </Typography>
        <Typography variant="body2">
          Select only the templates your site needs. You can always add more later by
          regenerating your theme from the wizard.
        </Typography>
      </Alert>

      {/* ── Core / Customizable templates ─────────────────────────────────── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1" fontWeight="bold">
          Core Templates — Fully Customizable
        </Typography>
        <Chip
          label="Homepage always included"
          size="small"
          icon={<LockIcon fontSize="small" />}
          variant="outlined"
          color="primary"
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <strong>{selectedCount}</strong> template{selectedCount !== 1 ? 's' : ''} selected
      </Typography>

      <Grid container spacing={2}>
        {CORE_TEMPLATES.map((template) => {
          const isSelected = value.includes(template.id);
          const isRequired = template.required;

          return (
            <Grid item xs={12} sm={6} md={4} key={template.id}>
              <Tooltip
                title={isRequired ? 'Homepage template is always included' : ''}
                placement="top"
                disableHoverListener={!isRequired}
              >
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    borderWidth: isSelected ? 2 : 1,
                    bgcolor: isSelected ? 'primary.50' : 'background.paper',
                    transition: 'border-color 0.15s, background-color 0.15s'
                  }}
                >
                  <CardActionArea
                    onClick={() => handleToggle(template.id, isRequired)}
                    sx={{ height: '100%' }}
                    disabled={isRequired}
                  >
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Typography variant="h5" component="span">
                          {template.icon}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {isRequired && (
                            <Chip label="Required" size="small" color="primary" sx={{ fontSize: '0.65rem', height: 18 }} />
                          )}
                          {isSelected
                            ? <CheckCircleIcon color="primary" fontSize="small" />
                            : <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                          }
                        </Box>
                      </Box>

                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        {template.name}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {template.description}
                      </Typography>

                      <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                        {template.wpFile}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>

      {/* ── Industry-specific page templates ───────────────────────────────── */}
      {industry && (
        <>
          <Divider sx={{ my: 3 }} />

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Industry Page Templates
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pre-built pages tailored for your industry — added to your theme as ready-to-use templates
              </Typography>
            </Box>
            {loadingIndustry && <CircularProgress size={20} />}
          </Box>

          {!loadingIndustry && industryPages.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
              No additional industry pages available.
            </Typography>
          )}

          {!loadingIndustry && Object.entries(industryByCategory).map(([category, pages]) => {
            const selectedInCat = pages.filter(p => value.includes(p.id)).length;
            return (
              <Accordion key={category} disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={1} width="100%">
                    <Typography variant="body2" fontWeight="medium" sx={{ flexGrow: 1, textTransform: 'capitalize' }}>
                      {category.replace(/-/g, ' ')}
                    </Typography>
                    {selectedInCat > 0 && (
                      <Chip label={`${selectedInCat} selected`} size="small" color="primary" variant="outlined" />
                    )}
                    <Button
                      size="small"
                      variant="text"
                      onClick={(e) => { e.stopPropagation(); handleSelectAllCategory(pages); }}
                      sx={{ minWidth: 'auto', fontSize: '0.7rem' }}
                    >
                      {pages.every(p => value.includes(p.id)) ? 'Deselect all' : 'Select all'}
                    </Button>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Grid container spacing={1.5}>
                    {pages.map(page => {
                      const isSelected = value.includes(page.id);
                      return (
                        <Grid item xs={12} sm={6} md={4} key={page.id}>
                          <Card
                            variant="outlined"
                            sx={{
                              borderColor: isSelected ? 'primary.main' : 'divider',
                              borderWidth: isSelected ? 2 : 1,
                              bgcolor: isSelected ? 'primary.50' : 'background.paper',
                              cursor: 'pointer',
                              transition: 'border-color 0.15s, background-color 0.15s'
                            }}
                            onClick={() => handleToggle(page.id, false)}
                          >
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                <Typography variant="body2" fontWeight="medium">
                                  {page.name}
                                </Typography>
                                {isSelected
                                  ? <CheckCircleIcon color="primary" fontSize="small" />
                                  : <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                                }
                              </Box>
                              {page.description && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                  {page.description}
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </>
      )}

      <Alert severity="success" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>After installing your theme in WordPress:</strong> go to{' '}
          <em>Pages → Add New</em> to create real pages and assign them to your
          chosen templates using the <em>Page Attributes</em> panel.
        </Typography>
      </Alert>
    </Box>
  );
};

export { CORE_TEMPLATES, REQUIRED_TEMPLATE_IDS, SUPPLEMENTAL_TEMPLATES };
export default TemplateSelection;
